/* eslint-disable react-hooks/exhaustive-deps */
import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import VirtualizedShopCarousel from '@/components/feature/shop/shop-carousel/VirtualizedShopCarousel';
import { MinimalPoiModal } from '@/components/feature/floor-guide/floor-guide-poi-modal';
import ShopPoiModal from '@/components/feature/shop/shop-poi-modal';
import ShopSearch from '@/components/feature/shop/shop-search';
import { BuildingMenuType } from '@/enums';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore, useShopStore } from '@/stores';
import type { PoiContentSlide } from '@/types/shop';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ShopPage.module.scss';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import { FloorGuideType } from '@/enums/floor-guide';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { isMinimalPoiSlide } from '@/utils/floor-guide/isMinimalPoiSlide';

const ShopPage = () => {
    const ui = useConfigStore((state) => state.config.ui);
    const server = useConfigStore((state) => state.config.server);
    const { isHorizontal } = useOrientation();
    const { t } = useTranslation();
    const shopContents = useShopStore((state) => state.shopContents);
    const rawShopContents = useShopStore((state) => state.rawShopContents);
    const currentMode = useShopStore((state) => state.currentMode);
    const updateShopContents = useShopStore((state) => state.updateShopContents);
    const initialize = useShopStore((state) => state.initialize);
    const setTranslationFunction = useShopStore((state) => state.setTranslationFunction);
    const resetFilters = useShopStore((state) => state.resetFilters);

    const [isOpenPoiModal, setIsOpenPoiModal] = useState(false);
    const [poiContent, setPoiContent] = useState<PoiContentSlide | null>(null);
    const { setSelectedPoi, setFloorGuideType } = useFloorGuideStore();
    const navigate = useNavigate();

    const isInitializedRef = useRef(false);
    const prevLanguageRef = useRef(ui.language);

    useEffect(() => {
        if (!isInitializedRef.current) {
            resetFilters();
            initialize(t);
            isInitializedRef.current = true;
            TrackingService.clearSearchKeyword();
        }
    }, [t, server?.buildingMenus]);

    useEffect(() => {
        if (prevLanguageRef.current !== ui.language) {
            setTranslationFunction(t);
            prevLanguageRef.current = ui.language;
        }
    }, [t, ui.language]);

    useEffect(() => {
        if (rawShopContents.length > 0) {
            updateShopContents(currentMode);
        }
    }, [rawShopContents, currentMode]);

    const isVerticalFullscreen = !isHorizontal && ui.screenMode !== 'split';

    const pageClassname = useMemo(() => {
        return clsx(styles['shop'], {
            [styles['shop__vertical']]: !isHorizontal,
            [styles['shop__horizontal']]: isHorizontal,
            [styles['shop__contrast']]: ui.contrast,
            [styles['shop__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['shop__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
        });
    }, [ui.screenMode, ui.contrast, isHorizontal]);

    const carouselClassname = useMemo(() => {
        return clsx(styles['shop__carousel'], {
            [styles['shop__carousel__horizontal']]: isHorizontal,
            [styles['shop__carousel__vertical']]: !isHorizontal,
            [styles['shop__carousel__contrast']]: ui.contrast,
            [styles['shop__carousel__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['shop__carousel__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
            [styles['shop__carousel__vertical__empty']]: shopContents.length === 0 && !isHorizontal,
            [styles['shop__carousel__vertical__empty__halfmode']]: shopContents.length === 0 && !isHorizontal && ui.screenMode === 'split',
            [styles['shop__carousel__horizontal__empty']]: shopContents.length === 0 && isHorizontal,
            [styles['shop__carousel__horizontal__empty__halfmode']]: shopContents.length === 0 && isHorizontal && ui.screenMode === 'split',
        });
    }, [isHorizontal, ui.screenMode, ui.contrast, shopContents.length]);

    const currentMenu = useMemo(() => {
        const listMenus = server?.buildingMenus?.[ui.language] || [];
        const currentMenu = listMenus.find((menu) => menu.menuType === BuildingMenuType.SEARCH);
        return currentMenu;
    }, [server?.buildingMenus, ui.language]);

    const onClickPoiContent = (data: PoiContentSlide) => {
        setPoiContent(data);
        setIsOpenPoiModal(true);
        TrackingService.trackPoi(data.data.id, undefined, data.data);
    };

    const handleLocationCheckShop = () => {
        if (!poiContent) return;
        setSelectedPoi({
            studioFloorID: poiContent.data.studioFloorId,
            studioPoiId: poiContent.data.studioPoiId,
            buildingId: poiContent.data.studioBuildingId,
            contentId: poiContent.data.id,
        });
        setFloorGuideType(FloorGuideType.MARKER);
        navigate(PATHS.FLOOR_GUIDE.CURRENT_LOCATION(poiContent.data.studioFloorId, poiContent.data.studioPoiId));
    };

    const handleGuideStartShop = () => {
        if (!poiContent) return;
        setSelectedPoi({
            studioFloorID: poiContent.data.studioFloorId,
            studioPoiId: poiContent.data.studioPoiId,
            buildingId: poiContent.data.studioBuildingId,
            contentId: poiContent.data.id,
        });
        setFloorGuideType(FloorGuideType.DIRECTION);
        navigate(PATHS.FLOOR_GUIDE.DIRECTION_GUIDE(poiContent.data.studioFloorId, poiContent.data.studioPoiId));
    };

    const isMinimalPoiDetail = isMinimalPoiSlide(poiContent, ui.language);

    return (
        <div className={pageClassname}>
            <div
                className={clsx(styles['shop__header'], {
                    [styles['shop__header--horizontal']]: isHorizontal,
                    [styles['shop__header--vertical']]: !isHorizontal,
                    [styles['shop__header--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                })}
            >
                <ShopFnbFacilityHeader
                    title={(currentMenu?.name as string) || ''}
                    description={currentMenu?.description || ''}
                    isHorizontal={isHorizontal}
                    tabIndex={1}
                />
            </div>
            <div className={carouselClassname}>
                {shopContents.length > 0 ? (
                    <VirtualizedShopCarousel contents={shopContents} onClickPoiContent={onClickPoiContent} />
                ) : (
                    <ShopFnbFacilityCarouselEmpty
                        id="shop-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                    />
                )}
            </div>

            <div
                className={clsx(styles['shop__search'], {
                    [styles['shop__search--horizontal']]: isHorizontal,
                    [styles['shop__search--vertical']]: !isHorizontal,
                    [styles['shop__search--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                    [styles['shop__search--vertical__fullscreen']]: isVerticalFullscreen,
                })}
            >
                <ShopSearch isVerticalFullscreen={isVerticalFullscreen} />
            </div>

            {isOpenPoiModal &&
                (isMinimalPoiDetail ? (
                    <MinimalPoiModal
                        isOpen={isOpenPoiModal}
                        language={ui.language}
                        poiContent={poiContent}
                        onClose={() => setIsOpenPoiModal(false)}
                        onGuideStart={handleGuideStartShop}
                        onLocationCheck={handleLocationCheckShop}
                    />
                ) : (
                    <ShopPoiModal
                        isOpen={isOpenPoiModal}
                        language={ui.language}
                        poiContent={poiContent}
                        buildings={server?.store.buildings || []}
                        screenMode={ui.screenMode}
                        onClose={() => setIsOpenPoiModal(false)}
                        onGuideStart={handleGuideStartShop}
                        onLocationCheck={handleLocationCheckShop}
                    />
                ))}
        </div>
    );
};

export default ShopPage;
